import fetch from "cross-fetch";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLanguageState } from "../redux/selectors/language-selectors";
import { getVerificationConfigurationState } from "../redux/selectors/verification-configuration-selectors";
import { datadogRum } from '@datadog/browser-rum';
import { setVerificationConfigurationLoading } from "@redux/actions/verification-configuration-actions";
import { setCustomCssLoaded, setCustomFooterHtml, setCustomHeaderHtml } from "@redux/actions/common-actions";
import { Logger } from "@utils/logger";
import { transform } from "@iddataweb/idw-common-ui/utils";

export const useInitializeCustomContent = () => {
    const dispatch = useDispatch();
    const [cssLoadState, setCssLoadState] = useState({
        loaded: false,
        error: null
    });

    const [headerLoadState, setHeaderLoadState] = useState({
        loaded: false,
        error: null
    });

    const [footerLoadState, setFooterLoadState] = useState({
        loaded: false,
        error: null
    });

    const urlRegex = /(https?:\/\/[^\s]+)/;
    const languageState = useSelector(getLanguageState);
    const verificationConfigurationState = useSelector(getVerificationConfigurationState);

    const checkExternalCss = async () => {
        const properties = verificationConfigurationState.config?.properties;
        if (properties?.css) {
            Logger.info(`[App] Found external css config: ${properties.css}`);
            const existingStyle = document.getElementById(`external-css-file-${properties.css}`);

            if (existingStyle) {
                setCssLoadState({ loaded: true, error: null });
                dispatch(setCustomCssLoaded(true));
                return;
            }

            try {
                const response = await fetch(properties.css);
                if (response) {
                    const css = await response.text();
                    if (css) {
                        const output = transform(css, `#root-html`, '', true);
                        const style = document.createElement('style');
                        style.setAttribute("id", `external-css-file-${properties.css}`);
                        style.setAttribute("type", "text/css");
                        style.appendChild(document.createTextNode(output));
                        document.head.appendChild(style);
                    }
                    Logger.info("Load success when attempting to download external CSS");
                    Logger.info(`[App] External CSS loaded successfully from ${properties.css}`);
                    dispatch(setCustomCssLoaded(true));
                    console.log(`[App] External CSS loaded successfully from ${properties.css}`);
                    setCssLoadState({ loaded: true, error: null });
                } else {
                    Logger.error('Load error when attempting to download external CSS: Response Empty');
                    Logger.info(`[App] Could not load External loaded successfully because response was empty`);
                    dispatch(setCustomCssLoaded(false));
                    console.log(`[App] Could not load External loaded successfully because response was empty`);
                    setCssLoadState({ loaded: true, error: "Response was empty" });
                }
            } catch (error) {
                Logger.error('Load error when attempting to download external CSS: ', error);
                Logger.info(`[App] Could not load External loaded successfully because ${error}`);
                dispatch(setCustomCssLoaded(false));
                console.log(`[App] Could not load External loaded successfully because ${error}`);
                setCssLoadState({ loaded: true, error: error });
            }
        } else {
            setCssLoadState({ loaded: true, error: null });
        }
    };

    const checkCustomHeader = async () => {
        const properties = verificationConfigurationState.config?.properties;

        if (properties?.htmlHeader && urlRegex.test(properties.htmlHeader)) {
            console.log(`[App] fetching custom header`);

            try {
                const response = await fetch(properties.htmlHeader);
                if (response?.status === 200) {
                    const html = await response.text();
                    console.log(`[App] custom header converted to text`);
                    dispatch(setCustomHeaderHtml(html));
                    setHeaderLoadState({ loaded: true, error: null });
                } else {
                    Logger.error("[App] Error loading custom header from: ", properties.htmlHeader);
                    setHeaderLoadState({ loaded: true, error: "Error loading custom header." });
                }
            } catch (error) {
                console.log(`[App] custom header convertion error`);
                Logger.error(error.message, error);
                const dataDogError = new Error(`[App] custom header convertion error`);
                datadogRum.addError(dataDogError, { error: error });
                setHeaderLoadState({ loaded: true, error: error });
            }
        } else {
            setHeaderLoadState({ loaded: true, error: null });
        }
    };

    const checkCustomFooter = async () => {
        const properties = verificationConfigurationState.config?.properties;
        const urlRegex = /(https?:\/\/[^\s]+)/;

        if (properties?.footer && urlRegex.test(properties.footer)) {
            console.log(`[App] Loading custom footer`);
            try {
                const response = await fetch(properties.footer);
                if (response?.status === 200) {
                    const html = await response.text();
                    console.log(`[App] custom footer converted to text`);
                    dispatch(setCustomFooterHtml(html));
                    setFooterLoadState({ loaded: true, error: null });
                } else {
                    Logger.error("[App] Error loading custom footer from: ", properties.footer);
                    setFooterLoadState({ loaded: true, error: "Error Loading custom footer" });
                }
            } catch (error) {
                console.log(`[App] custom footer convertion error`);
                Logger.error(error.message, error);
                const dataDogError = new Error(`[App] custom footer convertion error`);
                datadogRum.addError(dataDogError, { error: error });
                setFooterLoadState({ loaded: true, error: error });
            }
        } else {
            setFooterLoadState({ loaded: true, error: null });
        }
    };

    const initializeCustomContent = async () => {
        Logger.info(`[App] Loading custom header and footer`);
        await checkCustomHeader();
        await checkCustomFooter();
        await checkExternalCss();
    };

    useEffect(() => {
        if (cssLoadState.loaded && headerLoadState.loaded && footerLoadState.loaded) {
            if (cssLoadState.error || headerLoadState.error || footerLoadState.error) {
                Logger.info(`[App] Checks completed with errors`);
                console.log(`App] All checks completed with errors`);
            } else {
                Logger.info(`[App] All checks completed with no error`);
                console.log(`[App] All checks completed with no error`);
                console.log(`[App] Set config loading to false`);
            }
            dispatch(setVerificationConfigurationLoading(false));
        }
    }, [cssLoadState, headerLoadState, footerLoadState]);

    useEffect(() => {
        if (languageState.initialized) {
            initializeCustomContent();
        }
    }, [languageState.initialized]);
};